// src/components/RegionConstants.js

/**
 * Constants for Region Form Components
 * Extracted from RegionForm.js for better maintainability
 */

// Bone Quality Options for different bone types
export const BONE_QUALITY_OPTIONS = {
    crestal: [
        "___________",
        "Thick, flat and well-defined", 
        "Thick, round and well-defined", 
        "Thick, sharp and well-defined",
        "Thin, flat and well-defined", 
        "Thin, round and well-defined", 
        "Thin, sharp and well-defined",
        "is not well-defined", 
        "is not defined due to presence of tooth", 
        "Is not defined due to the implant placement",
        "Other"
    ],
    buccal: [
        "___________",
        "Completely Resorbed",
        "Thick and intact", 
        "Thin and intact", 
        "is resorbed till middle 3rd of tooth",
        "is resorbed till apical 3rd of tooth", 
        "is resorbed till apex of the tooth",
        "Other"
    ],
    lingual: [
        "___________",
        "Completely Resorbed", 
        "Thick and intact", 
        "Thin and intact", 
        "is resorbed till middle 3rd of tooth",
        "is resorbed till apical 3rd of tooth", 
        "is resorbed till apex of the tooth",
        "Other"
    ],
    palatal: [
        "___________",
        "Completely Resorbed", 
        "Thick and intact", 
        "Thin and intact", 
        "is resorbed till middle 3rd of tooth",
        "is resorbed till apical 3rd of tooth", 
        "is resorbed till apex of the tooth",
        "Other"
    ],
    labial: [
        "___________",
        "Completely Resorbed", 
        "Thick and intact", 
        "Thin and intact", 
        "is resorbed till middle 3rd of tooth",
        "is resorbed till apical 3rd of tooth", 
        "is resorbed till apex of the tooth",
        "Other"
    ],
    cancellous: [
        "___________",
        "D1 - very dense trabecular pattern", 
        "D2 - dense trabecular pattern", 
        "D1 to D2 - dense trabecular pattern",
        "D2 to D3 moderately dense trabecular pattern",
        "D3 less dense trabecular pattern",
        "D4 sparse trabecular pattern", 
        "No trabecular pattern seen",
        "Other"
    ]
};

// Length Measurement Options by jaw type
export const LENGTH_MEASUREMENT_OPTIONS = {
    mandible: [
        "___", 
        "mandibular canal", 
        "mental foramen", 
        "lower border of mandible", 
        "Lingual Canal"
    ],
    maxilla: [
        "___", 
        "sinus floor", 
        "nasal fossa"
    ]
};

// Undercut Options for mandible regions
export const UNDERCUT_OPTIONS = [
    "___",
    "no prominent undercut",
    "no undercut", 
    "deep undercut", 
    "undercut lingually", 
    "undercut above the mandibular canal", 
    "undercut below the mandibular canal", 
    "undercut at the level of mandibular canal"
];

// Quality Assessment Options
export const QUALITY_OPTIONS = [
    "___",
    "good",
    "moderate", 
    "deficient",
    "severely deficient"
];

// Width Behavior Options
export const WIDTH_OPTIONS = [
    "___",
    "gradually increases as we go apically",
    "gradually decreases as we go apically",
    "gradually increases at 6mm and then tapering off at 10 mm",
    "then tapers at 6mm before widening at 10mm",
    "remains same as we go apically"
];

// Ridge Form Options
export const RIDGE_FORM_OPTIONS = [
    "___",
    "is straight",
    "slopes towards buccally",
    "slopes towards lingually",
    "slopes towards palatally",
    "slopes towards labially",
    "slightly sloped towards buccally",
    "slightly sloped towards lingually",
    "slightly sloped towards palatally",
    "slightly sloped towards labially"
];

// Region-specific configurations
export const REGION_TYPES = {
    // Regions that show buccal bone options
    BUCCAL_REGIONS: [18, 17, 16, 15, 14, 24, 25, 26, 27, 28, 48, 47, 46, 45, 44, 34, 35, 36, 37, 38],
    
    // Regions that show labial bone options  
    LABIAL_REGIONS: [13, 12, 11, 21, 22, 23, 43, 42, 41, 31, 32, 33],
    
    // Regions that can have mental foramen slices
    MENTAL_FORAMEN_REGIONS: [46, 45, 44, 34, 35, 36],
    
    // Regions that can have lingual canal slices
    LINGUAL_CANAL_REGIONS: [41, 31],
    
    // Regions that can have nasopalatine canal slices
    NASOPALATINE_REGIONS: [11, 21],
    
    // Regions that can have incisive branch measurements (mandible anterior/premolar)
    INCISIVE_BRANCH_REGIONS: [45, 44, 43, 42, 41, 31, 32, 33, 34, 35],
    
    // Maxilla regions (including pterygoid)
    MAXILLA_REGIONS: ['right_pterygoid', 'left_pterygoid', 18, 17, 16, 15, 14, 24, 25, 26, 27, 28, 13, 12, 11, 21, 22, 23],
    
    // Mandible regions
    MANDIBLE_REGIONS: [48, 47, 46, 45, 44, 34, 35, 36, 37, 38, 43, 42, 41, 31, 32, 33]
};

// Width type mappings for different regions
export const WIDTH_TYPE_MAP = {
    // Buccopalatal regions (maxilla posterior + pterygoid)
    BUCCOPALATAL: ['right_pterygoid', 'left_pterygoid', 18, 17, 16, 15, 14, 24, 25, 26, 27, 28],
    
    // Labiopalatal regions (maxilla anterior) 
    LABIOPALATAL: [13, 12, 11, 21, 22, 23],
    
    // Buccolingual regions (mandible posterior)
    BUCCOLINGUAL: [48, 47, 46, 45, 44, 34, 35, 36, 37, 38],
    
    // Labiolingual regions (mandible anterior)
    LABIOLINGUAL: [43, 42, 41, 31, 32, 33]
};

// Default form data structure
export const DEFAULT_FORM_DATA = {
    pathology_comments: '',
    length_crest: '',
    length_crest_to: '___',
    length_periapical: '',
    length_periapical_to: '___',
    length_lesion: '',
    length_quality: '___',
    ridge_form: '___',
    undercut: '___',
    additional_comments: '',
    width_crest: '',
    width_option: '___',
    width_quality: '___',
    bone_quality: {
        crestal: '___________', 
        buccal: '___________', 
        lingual: '___________', 
        palatal: '___________', 
        labial: '___________', 
        cancellous: '___________',
        otherComments: {}
    },
    length_incision: ''
};

// Default optional slice data structure
export const DEFAULT_OPTIONAL_SLICE_DATA = {
    showMentalForamen: false,
    showLingualCanal: false,
    showNasopalatine: false,
    sliceContent: '',
    sliceImageFile: null
};

// Default crop configuration
export const DEFAULT_CROP = {
    unit: '%',
    width: 30,
    height: 30,
    x: 25,
    y: 25
};

// Styling constants
export const REGION_FORM_STYLES = {
    sectionStyle: {
        border: '2px solid black',
        mb: '20px',
        p: '20px'
    },
    sectionTitleStyle: {
        fontSize: '18px',
        fontWeight: 'bold',
        mb: '15px',
        borderBottom: '1px solid black',
        pb: '10px'
    },
    imageContainerStyle: {
        border: '2px solid black',
        height: '250px',
        mb: '15px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'grey.200'
    },
    measurementTextStyle: {
        fontSize: '14px',
        border: '1px solid black',
        p: '10px'
    }
};

// Default text templates for optional slices
export const OPTIONAL_SLICE_TEMPLATES = {
    mentalForamen: "We see mental foramen in slice ____ to ____",
    lingualCanal: "We could see lingual canal in this region which shows in slice ______",
    nasopalatine: "We see nasopalatine canal or incisive nerve in this region which shows in slice ______"
};